import BaseModel from 'volta/models/base-model';
import { collectionCommand, resourceCommand as command } from 'volta/utils/api';
import { formatISODate } from "volta/utils/date-utils";

import { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { IResourceName } from 'volta/models/base-model';
import type { TResetDayPayload, TUpdateDayPayload } from 'volta/models/types/calendar';

export const WorkshopCalendarDayResourceName: IResourceName = {
  singular: 'workshopCalendarDay',
  plural: 'workshopCalendarDays'
};

export enum WorkshopCalendarDayEvents {
  CalendarUpdated = 'CalendarUpdated',
  DaysReset = 'DaysReset',
  WorkshopCalendarDayReset = 'WorkshopCalendarDayReset'
}

export enum WorkshopCalendarDayCommands {
  UpdateCalendar = 'UpdateCalendar',
  ResetDays = 'ResetDays',
  ResetOne = 'ResetOne'
}

export default class WorkshopCalendarDay extends BaseModel {
  /**
   * Create or update the workshop calendar days
   * @return The list of created or updated WorkshopCalendarDay models
   */
  static updateCalendar = collectionCommand<
    TUpdateDayPayload,
    WorkshopCalendarDay[],
    typeof WorkshopCalendarDay
  >(WorkshopCalendarDayCommands.UpdateCalendar);

  /**
   * Effectively deletes the models identified by the workshop id and dates
   * @return Done
   */
  static resetDays = collectionCommand<TResetDayPayload, DoneResult, typeof WorkshopCalendarDay>(
    WorkshopCalendarDayCommands.ResetDays
  );

  /**
   * Effectively deletes the model identified by the entityId
   * @return Done
   */
  resetOne = command<undefined, WorkshopCalendarDay>(WorkshopCalendarDayCommands.ResetOne);

  @attr('string') workshopId!: string;
  @attr('date') date!: Date;
  @attr('boolean') openForDemand!: boolean;
  @attr('boolean') openForReception!: boolean;

  get formattedDate() {
    return formatISODate(this.date);
  }

  get indexedDay() {
    const { openForDemand, openForReception } = this;
    return {
      [formatISODate(this.date)]: {
        openForDemand,
        openForReception
      }
    };
  }

  [Type] = 'workshop-calendar-day' as const;
}
